import React from 'react';
import {Provider} from 'react-redux';
import {Router} from 'react-router';
import {createBrowserHistory} from 'history';
import PropTypes from 'prop-types';
import {Provider as SGProvider} from '@te-digi/styleguide';
import FeatureFlagsProvider from '@common/providers/FeatureFlagsProvider';
import UserInformationProvider from '@common/providers/UserInformationProvider';
import KopaCodesProvider from '@common/providers/KopaCodesProvider';
import loadStyles from '@common/widget-api/loadStyles';
import EnsureUserSimple from '@common/components/EnsureUser';
import isEmpty from 'ramda/es/isEmpty';
import LocaleProvider from './LocaleProviderContainer';
import {
  loadInitialLocale,
  setSupportedLocales
} from '../ducks/intl/actionCreators';
import {fetchNoUser} from '../ducks/user/actionCreators';
import ConfigProvider from './ConfigProvider';
import {
  setApiPath,
  setSkillRecommendationApiPath,
  setWidgetId
} from '../ducks/api/actionCreators';
import {MOBILE_BREAKPOINT, LOCALES} from '../config';
import EnsureUser from './EnsureUser';
import widgetPropTypes from '../widgetPropTypes';
import {setNaviLinks} from '../ducks/navilinks';
import setValue from '../ducks/keyvalue/actionCreators/setValue';
import {BASENAME_KEY} from '../ducks/basename';
import {setConfig, setWidgetBreadcrumbs} from '../ducks/config';
import {CODES_KEY} from '../ducks/codes/constants';
import {CMS_TITLE_KEY} from '../ducks/cms';
import {ECOSYSTEM_API_KEY} from '@shared/ducks/organization/constants';
import createApiPath from './helpers/createApiPath';
import EnsureUserWithI18Next from './EnsureUserWithI18Next';

const historyRef = {history: null};

const ProvidersCreator = (createStore, opts) => {
  const {
    withStyles = true,
    withRouter = true,
    withLocale = true,
    withUserInfo = false,
    isPublic = false,
    withIntl = true, // true, unless otherwise stated
    kopaCodes = []
  } = opts || {};
  const store = createStore();
  const config = {MOBILE_BREAKPOINT, LOCALES};

  let styleLoader = () => {};

  if (process.env.NODE_ENV === 'production') {
    styleLoader = widgetPath => {
      if (withStyles !== false) {
        const BUILT_CSS_FILENAME = 'main.css';
        loadStyles(`${widgetPath}${BUILT_CSS_FILENAME}`);
      }
    };
  }

  class Providers extends React.Component {
    constructor(props) {
      super(props);
      if (withRouter) {
        this.history = createBrowserHistory({basename: props.basename || ''});
        historyRef.history = this.history;
      }
    }

    componentDidMount() {
      store.dispatch(setWidgetId(this.props.widgetId));
      store.dispatch(
        setApiPath(
          createApiPath(
            this.props.apiPath,
            this.props.defaultApi,
            this.props.config?.organizationId
          )
        )
      );
      store.dispatch(setConfig(this.props.config));
      store.dispatch(setValue(CODES_KEY, this.props.codesApiPath));
      store.dispatch(
        setValue(
          ECOSYSTEM_API_KEY,
          this.props.consentsandserviceprovidersApiPath
        )
      );
      store.dispatch(setValue(CMS_TITLE_KEY, this.props.cmsPageTitle));
      store.dispatch(
        setSkillRecommendationApiPath(this.props.skillRecommendationApiPath)
      );

      if (withRouter) {
        store.dispatch(setValue(BASENAME_KEY, this.props.basename || ''));
      }
      store.dispatch(setNaviLinks(this.props.links));
      if (withLocale) {
        store.dispatch(
          loadInitialLocale(
            this.props.localeApiPath,
            this.props.locale,
            this.props.localeModule
          )
        );
        if (isPublic) {
          store.dispatch(fetchNoUser());
        }
      }
      store.dispatch(setSupportedLocales());
      store.dispatch(setWidgetBreadcrumbs(this.props.widgetBreadcrumbs));
      styleLoader(this.props.widgetPath);
    }

    render() {
      const {children, links, locale, featureFlags, user} = this.props;
      const routerNode = withRouter ? (
        <Router history={this.history}>{children}</Router>
      ) : (
        children
      );
      const userInfoNode = withUserInfo ? (
        <UserInformationProvider apiUrl={links?.userinfo}>
          {routerNode}
        </UserInformationProvider>
      ) : (
        routerNode
      );
      const ensureUser = isPublic ? (
        userInfoNode
      ) : withIntl ? (
        typeof user === 'undefined' ? (
          <EnsureUser>{userInfoNode}</EnsureUser>
        ) : (
          <EnsureUserSimple user={user}>{userInfoNode}</EnsureUserSimple>
        )
      ) : (
        <EnsureUserWithI18Next>{userInfoNode}</EnsureUserWithI18Next>
      );
      const localeNode = withLocale ? (
        <LocaleProvider>{ensureUser}</LocaleProvider>
      ) : (
        ensureUser
      );

      const useKopaCodesProvider = !isEmpty(kopaCodes);
      const providersNode = useKopaCodesProvider ? (
        <KopaCodesProvider
          apiUrl={links?.kopaCodesApiPath}
          kopaCodesets={kopaCodes}
        >
          {localeNode}
        </KopaCodesProvider>
      ) : (
        localeNode
      );

      return (
        <FeatureFlagsProvider featureFlags={featureFlags}>
          <SGProvider locale={locale}>
            <ConfigProvider config={config}>
              <Provider store={store}>{providersNode}</Provider>
            </ConfigProvider>
          </SGProvider>
        </FeatureFlagsProvider>
      );
    }
  }

  Providers.propTypes = {
    children: PropTypes.node.isRequired,
    ...widgetPropTypes
  };

  return Providers;
};

export {ProvidersCreator as default, historyRef};
